import { useCallback, useMemo, useState } from "react";
import { Flex, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { usePublicMediaAssets, useTranslate } from "#src/hooks";
import { useResizeObserver } from "hooks/useResizeObserver";
import {
  TemplateDataContext,
  TemplateDataContextType,
  TemplateEditorStoreContext,
} from "providers/TemplateDataProvider/";
import { TemplatePreviewWithVideoControls } from "./TemplatePreviewWithVideoControls";
import { TemplateType } from "pages/media/components/storyboardEditor/types";
import { mapBrandkitTemplate } from "pages/media/components/storyboardEditor/helpers/mapToTemplateState";
import { loadImageRatio } from "pages/media/components/storyboardEditor/helpers/";
import { createTemplateEditorStore } from "pages/media/components/storyboardEditor/store/useTemplateEditorStore";
import { BrandKitAsset, PaletteResponse } from "pages/media/types/";
import { StagedBrandKitAsset } from "types/";
import { useBrandKit } from "hooks/useBrandKit/";
import { PreviewTitle } from "./PreviewTitle";

const noop = () => undefined;

const MOBILE_ASPECT_RATIO = 9 / 16;
const PREVIEWS_GAP = 16;
const PREVIEW_TITLE_HEIGHT = 24;
const OUTER_GAP = 20;
const FALLBACK_PREVIEW_HEIGHT = 280;
const FALLBACK_PREVIEW_WIDTH = Math.round(
  FALLBACK_PREVIEW_HEIGHT * MOBILE_ASPECT_RATIO
);

const MIN_PREVIEW_HEIGHT = 180;

type DraftBrandKitData = ReturnType<typeof useBrandKit>;

const resolvePreviewAsset = (
  brandKitAssets: BrandKitAsset[],
  stagedAssets: StagedBrandKitAsset[],
  purpose: string,
  type?: BrandKitAsset["type"]
): BrandKitAsset | undefined => {
  const staged = stagedAssets.find(
    (s) =>
      s.purpose === purpose &&
      (!type || s.type === type) &&
      typeof s.path === "string"
  );
  if (staged?.path) {
    return staged as BrandKitAsset;
  }
  return brandKitAssets.find(
    (a) => a.purpose === purpose && (!type || a.type === type)
  );
};

export const BrandKitPreview = ({
  pickedColors,
  fonts,
  brandKitAssets,
  stagedAssets,
}: {
  pickedColors: PaletteResponse;
  fonts: BrandKitAsset[];
  brandKitAssets: BrandKitAsset[];
  stagedAssets: StagedBrandKitAsset[];
}) => {
  const t = useTranslate();
  const { defaultPublicFont } = usePublicMediaAssets();
  const [templateSize, setTemplateSize] = useState({
    width: FALLBACK_PREVIEW_WIDTH,
    height: FALLBACK_PREVIEW_HEIGHT,
  });

  const handlePreviewsContainerResize = useCallback(
    ({ height }: { height: number }) => {
      const previewHeight = Math.max(
        MIN_PREVIEW_HEIGHT,
        Math.floor((height - PREVIEWS_GAP * 3 - PREVIEW_TITLE_HEIGHT * 2) / 2)
      );
      const previewWidth = Math.floor(previewHeight * MOBILE_ASPECT_RATIO);

      setTemplateSize((prev) =>
        prev.width === previewWidth && prev.height === previewHeight
          ? prev
          : { width: previewWidth, height: previewHeight }
      );
    },
    []
  );

  const previewsContainerRef = useResizeObserver(handlePreviewsContainerResize);

  const draftBrandKitData = useMemo<DraftBrandKitData>(() => {
    const findByPurpose = (purpose: string) =>
      resolvePreviewAsset(brandKitAssets, stagedAssets, purpose);
    const findFont = (purpose: "Title" | "Subtitles" | "Text") =>
      fonts.find((font) => font.purpose === purpose);

    return {
      brandKitPalette: pickedColors,
      logo: findByPurpose("Logo"),
      titleFont: findFont("Title"),
      subtitlesFont: findFont("Subtitles"),
      textFont: findFont("Text"),
      audio: findByPurpose("Music"),
      backgroundBrandKitPicture: resolvePreviewAsset(
        brandKitAssets,
        stagedAssets,
        "Background",
        "Picture"
      ),
      backgroundBrandKitVideo: resolvePreviewAsset(
        brandKitAssets,
        stagedAssets,
        "Background",
        "Video"
      ),
      intro: findByPurpose("Introduction"),
      transition: findByPurpose("Transition"),
      banner: findByPurpose("Banner"),
      closing: findByPurpose("Closing"),
      brandKit: undefined,
      brandKitName: undefined,
      isBrandKitLoading: false,
      refetchBrandKit: () => Promise.resolve(undefined as unknown as never),
    };
  }, [pickedColors, fonts, brandKitAssets, stagedAssets]);

  const logoPath = draftBrandKitData.logo?.path;
  const { data: logoRatio } = useQuery<number>({
    queryKey: ["imageRatio", "brandKitPreviewLogo", logoPath],
    queryFn: () => (logoPath ? loadImageRatio(logoPath) : Promise.resolve(0)),
    enabled: !!logoPath,
  });

  const { defaultChapter, defaultTransition } = useMemo(() => {
    const commonAssets = {
      brandKitData: draftBrandKitData,
      initialPresenter: undefined,
      defaultPublicFont,
      contact: undefined,
      presenterRatio: undefined,
      logoRatio,
    } as unknown as Parameters<typeof mapBrandkitTemplate>[2];
    const chapter = mapBrandkitTemplate(
      TemplateType.CHAPTER,
      MOBILE_ASPECT_RATIO,
      commonAssets
    );
    const transition = mapBrandkitTemplate(
      TemplateType.TRANSITION,
      MOBILE_ASPECT_RATIO,
      commonAssets
    );
    return {
      defaultChapter: chapter,
      defaultTransition: transition,
    };
  }, [draftBrandKitData, defaultPublicFont, logoRatio]);

  const store = useMemo(
    () =>
      createTemplateEditorStore({
        currentChapter: {
          sectionId: "",
          topic: "",
          type: TemplateType.CHAPTER,
        },
        templateState: defaultChapter,
        templates: new Map(),
        serverTemplates: new Map(),
        slideNameToTemplateIdMap: {},
        defaultChapter,
        defaultTransition,
        defaultIntro: defaultChapter,
        defaultClosing: defaultChapter,
      }),
    [defaultChapter, defaultTransition]
  );

  const dataContextValue = useMemo(
    () =>
      ({
        brandKitData: draftBrandKitData,
        placeholderAssets: {},
      }) as unknown as TemplateDataContextType,
    [draftBrandKitData]
  );

  return (
    <Flex
      vertical
      gap={OUTER_GAP}
      style={{ width: "100%", height: "100%", minHeight: 0, paddingTop: 16 }}
    >
      <PreviewTitle title={t("media.brandkit.edit.preview")} />
      <TemplateEditorStoreContext.Provider value={store}>
        <TemplateDataContext.Provider value={dataContextValue}>
          <Flex
            ref={previewsContainerRef}
            vertical
            gap={PREVIEWS_GAP}
            align="center"
            style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
          >
            <Typography.Title
              level={4}
              style={{
                fontWeight: 400,
                margin: 0,
                fontSize: "17px",
                height: PREVIEW_TITLE_HEIGHT,
              }}
            >
              {t(`media.brandkit.preview.transitionPreview`)}
            </Typography.Title>
            <TemplatePreviewWithVideoControls
              chapter={{
                sectionId: "preview-transition",
                topic: "",
                type: TemplateType.TRANSITION,
                templateId: undefined,
              }}
              previewText={t("media.brandkit.preview.titleSample")}
              templateSize={templateSize}
              border={{
                borderWidth: 0,
                borderColor: undefined,
                borderRadius: 0,
              }}
              opacity={1}
              onCardClick={noop}
            />
            <Typography.Title
              level={4}
              style={{
                fontWeight: 400,
                margin: 0,
                fontSize: "17px",
                height: PREVIEW_TITLE_HEIGHT,
              }}
            >
              {t(`media.brandkit.preview.chapterPreview`)}
            </Typography.Title>
            <TemplatePreviewWithVideoControls
              chapter={{
                sectionId: "preview-chapter",
                topic: "",
                type: TemplateType.CHAPTER,
                templateId: undefined,
              }}
              previewText={t("media.brandkit.preview.subtitleSample")}
              templateSize={templateSize}
              border={{
                borderWidth: 0,
                borderColor: undefined,
                borderRadius: 0,
              }}
              opacity={1}
              onCardClick={noop}
            />
          </Flex>
        </TemplateDataContext.Provider>
      </TemplateEditorStoreContext.Provider>
    </Flex>
  );
};
